/**
 * Английские естественные предложения для переноса навыка.
 *
 * Отбор тот же, что и в русском наборе: только осмысленные фразы из
 * общеупотребительной лексики (ТЗ §4.6, §8.3). Сокращения с апострофом
 * оставлены намеренно — без них навык не переносится в реальный текст.
 */

import type { SentenceEntry } from './types.js';

/** Собирает запись предложения: язык подставляется один раз на весь список. */
function en(text: string, focusWords: readonly string[]): SentenceEntry {
  return { text, language: 'en', focusWords };
}

export const EN_SENTENCES: readonly SentenceEntry[] = [
  en('I work on this every single day.', ['work', 'on', 'this', 'day']),
  en("He took the book and didn't say a word.", [
    'he',
    'take',
    'the',
    'and',
    "didn't",
    'say',
    'a',
    'word',
  ]),
  en('We will be home late this evening.', ['we', 'will', 'be', 'home', 'late', 'this', 'evening']),
  en("She said that it's going to rain tomorrow.", [
    'she',
    'say',
    'that',
    "it's",
    'to',
    'tomorrow',
  ]),
  en('This is a simple question with a hard answer.', [
    'this',
    'a',
    'simple',
    'question',
    'with',
    'answer',
  ]),
  en('Speed grows only after accuracy is high.', ['speed', 'only', 'after', 'high']),
  en('A new day starts with the same small habits.', [
    'new',
    'day',
    'with',
    'the',
    'small',
    'habit',
  ]),
  en('They read one page every hour.', ['they', 'read', 'one', 'hour']),
  en('We lived in that city for three years.', ['we', 'in', 'that', 'city', 'for', 'year']),
  en('Your fingers find the right keys on their own.', [
    'your',
    'find',
    'the',
    'right',
    'their',
    'on',
  ]),
  en('Focus matters more than how fast you can move.', ['focus', 'than', 'how', 'you', 'can']),
  en('I made fewer errors today than yesterday.', ['make', 'error', 'today', 'yesterday', 'than']),
  en("Let's keep the lines short and clear.", ["let's", 'line', 'short', 'and', 'clear']),
  en('A good habit forms slowly and quietly.', ['good', 'habit', 'and', 'quiet']),
  en("You can't rush what memory has to learn.", [
    'you',
    "can't",
    'what',
    'memory',
    'have',
    'to',
    'learn',
  ]),
  en('Most of my mistakes come from hurry.', ['most', 'of', 'my', 'come', 'from']),
  en('It is not about speed at all.', ['it', 'not', 'about', 'speed', 'at', 'all']),
  en('Who told him about the change?', ['who', 'him', 'about', 'change']),
  en('There is no reason to look at the keyboard.', [
    'there',
    'no',
    'reason',
    'to',
    'look',
    'keyboard',
  ]),
  en('But his hands already know the way.', ['but', 'his', 'hand', 'know', 'way']),
  en('Would you like to try again?', ['would', 'you', 'like', 'to', 'again']),
  en('So we made a small plan for the week.', ['so', 'we', 'make', 'small', 'for', 'week']),
  en('Get up and take a short break.', ['get', 'up', 'take', 'short']),
  en('What do you want to learn first?', ['what', 'do', 'you', 'want', 'learn', 'first']),
  en('If they go slow, the errors go away.', ['if', 'they', 'go', 'slow', 'error']),
  en('Which line gave you the most trouble?', ['which', 'line', 'give', 'you', 'most']),
  en('By the end of the month it feels easy.', ['by', 'the', 'of', 'month', 'it']),
  en('Some people learn better in the morning.', ['some', 'people', 'learn', 'in', 'morning']),
  en('An hour of practice beats a long night.', ['an', 'hour', 'practice', 'long', 'night']),
  en('Or you can simply start over.', ['or', 'you', 'can', 'over']),
  en('Her answer was clear and short.', ['her', 'answer', 'clear', 'short']),
  en('As soon as you relax, the speed comes back.', ['as', 'speed', 'come', 'back']),
  en('My hands know where every letter lives.', ['my', 'hand', 'know', 'letter']),
  en('When the room is quiet, focus is easy.', ['when', 'quiet', 'focus']),
  en('Just one more line and we are done.', ['just', 'one', 'line', 'we']),
  en('Time on the keyboard turns into memory.', ['time', 'on', 'keyboard', 'into', 'memory']),
  en("That's the only way to make it stick.", ["that's", 'only', 'way', 'make', 'it']),
  en('Do not look down at your hands.', ['do', 'not', 'look', 'at', 'your', 'hand']),
  en('He said it out loud and it stayed with me.', ['he', 'say', 'it', 'out', 'with', 'me']),
  en('All of them use the same simple rule.', ['all', 'of', 'them', 'use', 'simple']),
  en('Take a slow breath before the next line.', ['take', 'slow', 'before', 'line']),
  en('You will find the letter without looking.', ['you', 'will', 'find', 'letter', 'without']),
  en('People often stop right before it gets easy.', ['people', 'often', 'right', 'before']),
  en('Give it two more weeks.', ['give', 'two', 'week']),
  en('My friend and I practice together every evening.', [
    'my',
    'friend',
    'practice',
    'together',
    'evening',
  ]),
  en('The best result is a quiet, steady rhythm.', ['the', 'result', 'quiet']),
  en('Every session leaves a small trace in memory.', ['session', 'small', 'memory', 'in']),
  en('Our target for today is one clear line.', ['our', 'target', 'today', 'clear', 'line', 'one']),

  // Sentences carrying punctuation: mode 9 trains marks inside real text.
  en('He said: "Come back tomorrow", and left.', ['he', 'say', 'come', 'back']),
  en('Some are ready; others still need time.', ['some', 'need', 'time']),
  en('What a good day!', ['what', 'a', 'good', 'day']),
  en('Well-known words come first (the rest can wait).', ['well', 'word', 'come', 'first', 'can']),
  en('You need only one thing: patience.', ['you', 'need', 'only', 'one', 'thing']),
  en('She asked about it - and no one answered.', ['she', 'about', 'it', 'no', 'one']),
  en('The word "hand" is here for a reason.', ['the', 'word', 'hand', 'here', 'for', 'a']),
  en('Read it again; then write it down.', ['read', 'write', 'it', 'then']),
  en('First place - and no errors at all!', ['first', 'place', 'no', 'error', 'at', 'all']),
  en('He thought: time will do the rest.', ['he', 'think', 'time', 'will', 'do', 'the']),
  en('At the end of the line (with a tired hand) errors grow.', [
    'at',
    'the',
    'of',
    'line',
    'hand',
    'error',
  ]),
  en('We know what to do: repeat, and do not rush.', ['we', 'know', 'what', 'to', 'do', 'not']),
  en('It is a well-made keyboard; the letters are clear.', ['it', 'a', 'keyboard', 'letter']),
  en('Is that your work, or his?', ['that', 'your', 'work', 'or', 'his']),

  en('The morning started with a glass of water.', ['morning', 'start', 'water']),
  en('The road to work takes almost an hour.', ['road', 'work', 'hour']),
  en('He held the pen too tightly and got tired.', ['hold', 'pen']),
  en('Put the chair straight and drop your shoulders.', ['put', 'chair']),
  en('The palms rest calmly and the fingers stay loose.', ['palm', 'calmly', 'finger', 'rest']),
  en('Each finger answers for its own row of keys.', ['finger', 'answer', 'own']),
  en('A steady rhythm beats a short burst.', ['rhythm', 'short']),
  en('Pace grows on its own when accuracy holds.', ['pace', 'grow', 'hold']),
  en('A small pause helps more than stubbornness.', ['pause', 'help', 'more']),
  en('Repeats belong to hard words, not to every word.', ['repeat', 'word']),
  en('A review of mistakes takes five minutes a day.', ['review', 'minute', 'day']),
  en('We compared two ways and chose the simple one.', ['compare', 'two', 'choose', 'simple']),
  en('She noticed that she looks at the keys less often.', ['notice', 'look', 'less', 'often']),
  en('The student asked and the teacher answered at once.', [
    'student',
    'ask',
    'teacher',
    'answer',
  ]),
  en('The doctor advised a break every hour.', ['doctor', 'hour']),
  en('My sister called late in the evening.', ['sister', 'call', 'late', 'evening']),
  en('My brother promised to help on Saturday.', ['brother', 'promise', 'help']),
  en('The son reads a book aloud almost every evening.', ['son', 'read', 'book', 'evening']),
  en('The daughter drew a house, a garden and a large sun.', [
    'daughter',
    'garden',
    'large',
    'sun',
  ]),
  en('Mother put the tea on the table.', ['mother', 'tea', 'table', 'put']),
  en('Father brought bread and milk from the shop.', ['father', 'bread', 'milk', 'shop']),
  en('The neighbour left a note under the door.', ['door', 'under']),
  en('The guest took off his coat and sat closer to the window.', ['guest', 'window', 'closer']),
  en('Friends gathered in the yard after lunch.', ['friend', 'lunch']),
  en('The team finished the project a week early.', ['team', 'project', 'week', 'early']),
  en('The driver waited at the gate for half an hour.', ['driver', 'half', 'hour']),
  en('The contract was signed at the end of the month.', ['contract', 'end', 'month']),
  en('The price turned out lower than we thought.', ['price', 'lower', 'think']),
  en('The goods sat on the top shelf.', ['goods', 'shelf']),
  en('The choice was simpler than it seemed at first.', ['choice', 'first']),
  en('The address is written on the back of the page.', ['address', 'write', 'page']),
  en('The phone died at the worst possible moment.', ['phone']),
  en('The program starts with a single button.', ['program', 'start', 'button']),
  en('The file was saved but the folder stayed empty.', ['file', 'folder']),
  en('The network was slow, yet the letter went out.', ['network', 'slow', 'letter']),
  en('The page opened only on the third try.', ['page', 'open', 'try']),
  en('The site looks simpler on a small screen.', ['site', 'small']),
  en('The computer stands by the window, left of the table.', [
    'computer',
    'window',
    'table',
    'stand',
  ]),
  en('The river was quiet and the shore was empty.', ['river', 'quiet', 'shore']),
  en('The forest began right behind the field.', ['forest', 'field']),
  en('The sea made noise all night.', ['sea', 'noise', 'night']),
  en('The hill looked closer than it really was.', ['hill', 'closer']),
  en('The sky cleared by the evening.', ['sky', 'evening']),
  en('The wind tore off the last leaves.', ['wind', 'last']),
  en('It rained the whole way to the city.', ['rain', 'way', 'city']),
  en('Snow lay only on the north side of the yard.', ['snow']),
  en('The ice on the puddle cracked under a foot.', ['ice', 'under']),
  en('The fire in the stove had almost gone out.', ['fire']),
  en('Light from the window fell on the wall.', ['light', 'window', 'wall', 'fall']),
  en('The shade of the tree moved with the sun.', ['shade', 'sun']),
  en('The sound was quiet but easy to recognise.', ['sound', 'quiet']),
  en('The voice on the phone seemed familiar.', ['voice', 'phone']),
  en('Noise from the street made it hard to think.', ['noise', 'street', 'think']),
  en('The smell of bread reaches the door.', ['smell', 'bread', 'door']),
  en('The taste of tea depends on the water.', ['taste', 'tea', 'water']),
  en('The salt stood next to the sugar.', ['salt', 'stand']),
  en('Lunch was ready before we came back.', ['lunch', 'before', 'back']),
  en('Dinner passed quietly and quickly.', ['dinner', 'quick']),
  en('The table turned out too high for this chair.', ['table', 'chair', 'high']),
  en('The door closed by itself.', ['door', 'close']),
  en('The window looks out on the yard.', ['window', 'look']),
  en('The wall was thinner than the neighbour thought.', ['wall', 'think']),
  en('The floor under the carpet was cold.', ['floor', 'under', 'cold']),
  en('A bookcase took the corner of the room.', ['corner', 'take']),
  en('The shelf holds on three screws.', ['shelf', 'hold']),
  en('The bag stood by the entrance all day.', ['bag', 'stand', 'day']),
  en('A key and a coin were found in the pocket.', ['key', 'pocket', 'find']),
  en('The paper ran out at the worst moment.', ['paper']),
  en('The notebook is open in the middle.', ['notebook', 'open', 'middle']),
  en('The letter arrived a week later.', ['letter', 'week']),
  en('The magazine turned out old but interesting.', ['magazine', 'old']),
  en('The story is short and stays in memory for long.', ['story', 'short', 'memory', 'long']),
  en('The song is familiar to almost everyone.', ['song']),
  en('The game ended in a draw.', ['game', 'end']),
  en('Sport teaches order better than talking does.', ['sport', 'order', 'better']),
  en('A morning run became a habit.', ['run', 'habit', 'morning']),
  en('Sleep matters more than one extra hour of work.', ['sleep', 'more', 'hour', 'work']),
  en('Rest is needed no less than practice.', ['rest', 'less', 'practice']),
  en('Health is worth more than any speed.', ['health', 'speed']),
  en('The family gathers at the table on Sundays.', ['family', 'table']),
  en('The rule is simple: accuracy first, speed later.', ['rule', 'simple', 'speed']),
  en('The reason was not in the keyboard.', ['reason', 'keyboard']),
  en('An example helps more than a long explanation.', ['example', 'help', 'long']),
  en('There is one way to check: type an unfamiliar text.', ['way', 'check', 'one']),
  en('The decision was not made at once.', ['decision']),
  en('I learned the meaning of this word only yesterday.', [
    'meaning',
    'word',
    'learn',
    'yesterday',
  ]),
  en('The condition is simple, the work is not.', ['condition', 'simple', 'work']),
  en('Quality matters more than the number of repeats.', ['quality', 'number', 'repeat']),
  en('Experience comes with the number of attempts.', ['experience', 'number']),
  en('The movement of the fingers became visibly evener.', ['movement', 'finger']),
  en('The order of keys is remembered by hands, not by eyes.', ['order', 'remember', 'hand']),
  en('The check showed that the speed had grown.', ['check', 'speed', 'grow']),
  en('A session lasts exactly five minutes.', ['session', 'exactly', 'minute']),
  en('The lesson ended before the bell.', ['lesson', 'end', 'before']),
  en('The task is solved in two steps.', ['task', 'two', 'step']),
  en('The goal for today is modest and reachable.', ['goal', 'today']),
  en('The plan for the week fits in a single line.', ['plan', 'week', 'line']),
  en('Step by step works faster than in bursts.', ['step', 'fast']),
  en('The level rises when the mistakes fall.', ['level', 'error']),
];
